import React, {useMemo} from 'react';
import {BarChart2, CalendarDays, PieChart as PieIcon} from 'lucide-react';
import type {Holiday} from "../../../domains/models/holiday";
import {holidayTypeLabel, MONTH_NAMES, WEEKDAY_NAMES} from "../holiday.view";
import dayjs from "dayjs";
import "../../../styles/component/common/chart.scss";
import StatsChart from "../../common/stats-chart/stats-chart";
import HolidayBarChart from "./holiday-bar-chart";
import HolidayPieChart from "./holiday-pie-chart";

interface HolidayChartsRowProps {
    holidays: Holiday[];
    loading: boolean;
    year: number;
}

function HolidayChartsRow({holidays, loading, year}: HolidayChartsRowProps) {

    console.log("Holidays: ", holidays);

    // Holiday types distribution data
    const typeChartData = useMemo(() => {
        return Object.entries(
            holidays.reduce<Record<string, number>>((acc, holiday) => {
                acc[holiday.type] = (acc[holiday.type] || 0) + 1;
                return acc;
            }, {})
        ).map(([type, count]) => ({
            name: holidayTypeLabel(type),
            count,
        }));
    }, [holidays]);

    // Holiday monthly distribution data
    const monthChartData = useMemo(() => {
        return MONTH_NAMES.map((month, index) => ({
            // name: month.slice(0, 3),
            name: month,
            count: holidays.filter((holiday) => dayjs(holiday.date).month() === index).length,
        }));
    }, [holidays]);

    // Holiday weekly distribution data
    const weekdayChartData = useMemo(() => {
        return WEEKDAY_NAMES.map((day, index) => ({
            // name: day.slice(0, 3),
            name: day,
            count: holidays.filter(
                (holiday) => dayjs(holiday.date).day() === index
            ).length,
        }));
    }, [holidays]);

    return (
        <>
            {/* Holiday monthly distribution - Bar chart */}
            <div className={'holiday-monthly-chart'}>
                <StatsChart
                    icon={BarChart2}
                    title={`Monthly Holiday Distribution — ${year}`}
                    badge={{text: `Total ${holidays.length} holidays`, color: "green"}}
                >
                    {loading ? (
                        <div
                            className={'pn-chart__skeleton-cell'}
                            style={{height: 220, width: '100%', borderRadius: 8}}
                        />
                    ) : (
                        <HolidayBarChart data={monthChartData}/>
                    )}
                </StatsChart>
            </div>


            <div className={'pn-chart__grid two-column'}>
                {/* Holiday weekly distribution - Bar chart */}
                <StatsChart
                    icon={BarChart2}
                    title="Holidays by day of week"
                >
                    {loading ? (
                        <div
                            className={'pn-chart__skeleton-cell'}
                            style={{height: 220, width: '100%', borderRadius: 8}}
                        />
                    ) : (
                        <HolidayBarChart data={weekdayChartData}/>
                    )}
                </StatsChart>

                {/* Holiday types distribution - Pie chart */}
                <StatsChart
                    icon={PieIcon}
                    title="Holidays type split"
                >
                    {loading ? (
                            <div
                                className={'pn-holiday__skeleton-cell'}
                                style={{height: 220, width: '100%', borderRadius: 8}}
                            />
                        ) :
                        holidays.length === 0 ? (
                                <div className={'pn-holiday__cal-chart-empty'}>
                                    <CalendarDays size={24}/>
                                    <p>No holidays for {year}</p>
                                </div>
                            ) :
                            <HolidayPieChart data={typeChartData}/>
                    }
                </StatsChart>
            </div>
        </>
    );
}

export default HolidayChartsRow;